import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { lessonTypeaheadSearch, useLessonById } from '@/lib/api-call-hooks';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { classTimeTypesFilterOption } from '@/lib/form-utils';
import { paginationRequestSchema } from '@/lib/pagination';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table';
import { FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import { AdminSession, classTimeTypeSchema } from '@airreading/common/data';
import { pluralize } from '@/lib/string-utils';
import { studentTabs } from '../../-components/tabs';
import { validateRouteRole } from '@/lib/routing';

export const Route = createFileRoute('/_layout/students/$studentId/future-sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentFutureSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    lessons: z.string().array().optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
    endDate: z.string().optional(),
  }),
});

function StudentFutureSessionsIndex() {
  const queryClient = useQueryClient();
  const { studentId } = Route.useParams();
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
      endDate: [[selectedEndDate], setSelectedEndDate],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'lessons', 'classTypes', 'endDate']);

  const startOfTomorrowInstantInCST = Temporal.Now.zonedDateTimeISO(FrequentTimeZones.UsCentral)
    .add({ days: 1 })
    .startOfDay()
    .toInstant();
  const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: startOfTomorrowInstantInCST.toString(),
        startOnOrBefore: endPlanDate
          ? endPlanDate
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : undefined,
        query: activeQuery,
        lessons: activeSelectedLessons,
        classTypes: activeSelectedClassTypes,
        studentIds: [studentId], // Filter by the current student ID
      },
    }),
  );

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const fetchLessonById = useLessonById();

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search sessions by student, email, or teacher"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Lesson Name"
          options={lessonTypeaheadSearch(queryClient)}
          selectedValues={selectedLessons ?? []}
          onChange={setSelectedLessons}
          getOptionSearchFn={fetchLessonById}
        />
        <TableFilterDropdown
          title="Class Type"
          options={classTimeTypesFilterOption}
          selectedValues={selectedClassTypes ?? []}
          onChange={setSelectedClassTypes}
        />
        <TableFilterPlainDatePicker
          selectedPlainDateString={selectedEndDate}
          onPlainDateSelect={setSelectedEndDate}
          tableFilterDatePickerProps={{
            placeholder: 'End date (CT)',
          }}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      queryClient,
      selectedLessons,
      setSelectedLessons,
      fetchLessonById,
      selectedClassTypes,
      setSelectedClassTypes,
      selectedEndDate,
      resetFilters,
      setQuery,
      setSelectedEndDate,
    ],
  );

  return (
    <InfoPage
      headerText="Future Sessions"
      tabs={studentTabs({ studentId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Students',
            link: {
              to: '/students',
            },
          },
        ],
        currentPage: 'Future Sessions',
      }}
    >
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onRowSelectionChange,
          getRowId,
          onPaginationChange,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        from="future"
      />
    </InfoPage>
  );
}
